import { useEffect } from "react"

import type { ToolApproval } from "@/api/models"

type UseToolApprovalKeyboardProps = {
  pendingApprovals: ToolApproval[]
  onApprove: (requestId: string, callId: string) => void | Promise<void>
  onDeny: (requestId: string, callId: string) => void | Promise<void>
  onApproveAll?: () => void | Promise<void>
  onDenyAll?: () => void | Promise<void>
  enabled: boolean
}

export function useToolApprovalKeyboard({
  pendingApprovals,
  onApprove,
  onDeny,
  onApproveAll,
  onDenyAll,
  enabled,
}: UseToolApprovalKeyboardProps) {
  useEffect(() => {
    if (!enabled || pendingApprovals.length === 0) {
      return
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement
      const isInputFocused =
        target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable

      if (isInputFocused) {
        return
      }

      const firstApproval = pendingApprovals[0]

      const key = event.key.toLowerCase()

      if (event.shiftKey && key === "a" && onApproveAll && pendingApprovals.length > 1) {
        event.preventDefault()
        void Promise.resolve(onApproveAll())
        return
      }

      if (event.shiftKey && key === "d" && onDenyAll && pendingApprovals.length > 1) {
        event.preventDefault()
        void Promise.resolve(onDenyAll())
        return
      }

      switch (key) {
        case "enter":
        case "a":
          event.preventDefault()
          // Arguments too large to persist were dropped from this event (the
          // tool still runs with its full original arguments after approval)
          // -- a keyboard shortcut must not approve blind any more than the
          // (disabled) Approve button does.
          if (!firstApproval.argumentsElided) {
            void Promise.resolve(onApprove(firstApproval.request_id, firstApproval.call_id))
          }
          break

        case "d":
        case "backspace":
          event.preventDefault()
          void Promise.resolve(onDeny(firstApproval.request_id, firstApproval.call_id))
          break

        default:
          if (key >= "1" && key <= "9") {
            const index = parseInt(key, 10) - 1
            if (index < pendingApprovals.length) {
              event.preventDefault()
              const approval = pendingApprovals[index]
              if (!approval.argumentsElided) {
                void Promise.resolve(onApprove(approval.request_id, approval.call_id))
              }
            }
          }
          break
      }
    }

    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [enabled, onApprove, onApproveAll, onDeny, onDenyAll, pendingApprovals])
}
